import { useState, useMemo } from 'react';
import { throttleToAnimationFrame } from '@utils/common';
import { useIsomorphicLayoutEffect } from '@utils/react';
import { sliderTokens } from './slider.styles.js';

/**
 * internal hook to keep track of pagination based on user interactions
 */
function usePagination(models, args) {
  const [activeIndex, setActiveIndex] = useState(args.initialIndex ?? 0);
  const [itemsPerPage, setItemsPerPage] = useState(0);
  useIsomorphicLayoutEffect(() => {
    const slider = args.sliderRef.current;
    if (!slider) {
      return;
    }
    const onResize = () => {
      const itemsPerPage = window.getComputedStyle(slider).getPropertyValue(sliderTokens.itemsPerPage.propName);
      const itemsPerPageNumber = parseFloat(itemsPerPage);
      if (!Number.isNaN(itemsPerPageNumber)) {
        setItemsPerPage(itemsPerPageNumber);
      } else {
        const sizer = document.createElement("div");
        sizer.classList.add("test");
        sizer.style.width = `calc(${itemsPerPage} * 1px)`;
        if (!sizer.style.width) {
          sizer.style.width = itemsPerPage;
        }
        document.body.appendChild(sizer);
        const resolvedItemsPerPage = sizer.clientWidth;
        setItemsPerPage(resolvedItemsPerPage);
      }
    };
    onResize();
    const observer = new ResizeObserver(throttleToAnimationFrame(onResize));
    observer.observe(slider);
    return () => {
      observer.disconnect();
    };
  }, [args.sliderRef]);
  return useMemo(() => {
    const pageCount = itemsPerPage ? Math.ceil(models.length / itemsPerPage) : 0;
    const activeItemIndex = args.type === "pages" ? Math.round(itemsPerPage * activeIndex) : activeIndex;
    const activePageIndex = args.type === "pages" ? activeIndex : activeIndex / itemsPerPage;
    const canGoBackwards = args.infinite || activeItemIndex !== 0;
    const canGoForwards = args.infinite || activeItemIndex < models.length - 1;
    return {
      type: args.type,
      itemsPerPage,
      activeItemIndex,
      activePageIndex,
      pageCount,
      goTo: index => {
        setActiveIndex(index);
      },
      goForwards: () => {
        if (!canGoForwards) {
          return;
        }
        setActiveIndex(prev => prev + 1);
      },
      goBackwards: () => {
        if (!canGoBackwards) {
          return;
        }
        setActiveIndex(prev => prev - 1);
      },
      canGoBackwards,
      canGoForwards
    };
  }, [activeIndex, args.infinite, args.type, itemsPerPage, models.length]);
}

export { usePagination };
//# sourceMappingURL=hook.pagination.js.map
